import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { ApiResponse, PageMeta } from '../types';

interface ApiState<T> {
  data: T | null;
  meta: PageMeta | null;
  loading: boolean;
  error: string | null;
}

type Query = Record<string, string | number | undefined | null>;

/** Data-fetching hook with loading/error state and re-fetch on `deps` change. */
export function useApi<T>(path: string, query?: Query, deps: unknown[] = []) {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    meta: null,
    loading: true,
    error: null,
  });
  const queryRef = useRef(query);
  queryRef.current = query;

  // Monotonic id of the latest request. Every refetch claims the next id and
  // only writes state if it is still the latest when it resolves, so a slow
  // response from an earlier query can never overwrite a newer one. The effect
  // cleanup bumps it too, so a resolution after unmount is discarded.
  const reqIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    const isCurrent = () => reqIdRef.current === reqId;
    setState((s) => ({ ...s, loading: true, error: null }));
    // An empty path means "skip" — used for conditional/dependent fetches.
    if (!path) {
      if (isCurrent()) setState({ data: null, meta: null, loading: false, error: null });
      return;
    }
    try {
      const res: ApiResponse<T> = await api.get<T>(path, queryRef.current);
      if (isCurrent()) setState({ data: res.data, meta: res.meta ?? null, loading: false, error: null });
    } catch (err) {
      if (isCurrent()) setState({ data: null, meta: null, loading: false, error: (err as Error).message });
    }
  }, [path]);

  useEffect(() => {
    void refetch();
    return () => {
      // Invalidate any in-flight request so its resolution is ignored.
      reqIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ...state, refetch };
}

/** Imperative mutation helper. */
export function useMutation() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async <R>(fn: (a: typeof api) => Promise<R>): Promise<R> => {
    setLoading(true);
    setError(null);
    try {
      return await fn(api);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { run, loading, error };
}
